import React, { useCallback, useMemo, useState } from "react";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
} from "../../../../global/directory";
import "../../../user-builder/styles/userBuilderTable.css";
import "../../../../global/directory/globalDirectory.css";
import MyMembershipsDetailModal from "./MyMembershipsDetailModal";
import {
  MY_MEMBERSHIPS_COLUMN_DEFS,
  MY_MEMBERSHIPS_PAGE_SIZE,
  MY_MEMBERSHIPS_VISIBILITY_KEY,
  buildValueOptionsFromRows,
  createInitialColumnVisibility,
  defaultMyMembershipsFilters,
  filterTimelineRows,
  membershipStatusBadgeClass,
  paginateList,
  timelineCellValue,
} from "../../utils/myMembershipsDirectoryHelpers";
import { normalizeTimelineRow } from "../../utils/myMembershipsOverviewHelpers";

export default function MyMembershipsTimelineSection({ timeline, loading }) {
  const rows = useMemo(
    () =>
      Array.isArray(timeline) ? timeline.map((row) => normalizeTimelineRow(row)) : [],
    [timeline]
  );
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState(defaultMyMembershipsFilters);
  const [page, setPage] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    createInitialColumnVisibility(
      MY_MEMBERSHIPS_VISIBILITY_KEY,
      MY_MEMBERSHIPS_COLUMN_DEFS
    )
  );

  const filtered = useMemo(
    () => filterTimelineRows(rows, filters),
    [rows, filters]
  );

  const { page: safePage, totalPages, total, slice } = useMemo(
    () => paginateList(filtered, page, MY_MEMBERSHIPS_PAGE_SIZE),
    [filtered, page]
  );

  const valueFilterOptions = useMemo(() => {
    const col = filters.column;
    if (!col) return [];
    return buildValueOptionsFromRows(rows, col);
  }, [filters.column, rows]);

  const visibleColumns = useMemo(
    () => MY_MEMBERSHIPS_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility]
  );

  const tableHeaders = useMemo(
    () => [
      ...visibleColumns.map((c) => ({ key: c.id, label: c.label })),
      { key: "actions", label: "Action", className: "ub-table__col-actions" },
    ],
    [visibleColumns]
  );

  const getExportData = useCallback(() => {
    const headers = visibleColumns.map((c) => c.label);
    const dataRows = filtered.map((row) =>
      visibleColumns.map((c) => timelineCellValue(c.id, row))
    );
    return { headers, rows: dataRows };
  }, [filtered, visibleColumns]);

  const showRows = !loading && slice.length > 0;

  if (!loading && rows.length === 0) {
    return (
      <section className="user-builder-panel mm-directory-wrap" aria-label="Membership directory">
        <div className="user-builder-panel__head">
          <h3 className="user-builder-panel__title">Membership directory</h3>
          <p className="user-builder-panel__hint">
            Search, filter, and browse your full membership history.
          </p>
        </div>
        <p className="mm-empty">No membership history available.</p>
      </section>
    );
  }

  return (
    <section className="user-builder-panel mm-directory-wrap" aria-label="Membership directory">
      <div className="user-builder-panel__head">
        <h3 className="user-builder-panel__title">Membership directory</h3>
        <p className="user-builder-panel__hint">
          Search, filter, and browse your full membership history with pagination.
        </p>
      </div>

      <GlobalDirectoryShell
        className="mm-directory-shell"
        filters={
          <GlobalDirectoryFiltersSection
            disabled={loading}
            className="ub-filters ub-user-directory-filters"
            searchLabel="Search memberships"
            searchPlaceholder="ID, plan, status, receipt, transaction…"
            searchValue={filters.search}
            onSearchChange={(v) => {
              setFilters((f) => ({ ...f, search: v }));
              setPage(1);
            }}
            columnLabel="Column"
            columnValue={filters.column}
            onColumnChange={(v) => {
              setFilters((f) => ({ ...f, column: v, value: "" }));
              setPage(1);
            }}
            gridColumnDefs={MY_MEMBERSHIPS_COLUMN_DEFS}
            valueLabel="Value"
            valueValue={filters.value}
            onValueChange={(v) => {
              setFilters((f) => ({ ...f, value: v }));
              setPage(1);
            }}
            valueOptions={valueFilterOptions}
            onClear={() => {
              setFilters(defaultMyMembershipsFilters());
              setPage(1);
            }}
            clearFilterLabel="Clear filter"
            secondaryStart={
              <GlobalDirectoryColumnPicker
                columnDefs={MY_MEMBERSHIPS_COLUMN_DEFS}
                visibility={columnVisibility}
                onVisibilityChange={setColumnVisibility}
                storageKey={MY_MEMBERSHIPS_VISIBILITY_KEY}
              />
            }
            secondaryEnd={
              <GlobalDirectoryExportMenu
                getExportData={getExportData}
                filenameBase={`my-memberships-${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`}
                buttonLabel="Export as"
              />
            }
          />
        }
        grid={
          <GlobalDirectoryGridSection
            loading={loading}
            loadingMessage="Loading membership directory…"
            headers={tableHeaders}
            isEmpty={!loading && filtered.length === 0}
            emptyMessage="No memberships match your filters."
            tableAriaLabel="My memberships directory"
            wrapClassName="ub-table-wrap"
            tableClassName="ub-table"
          >
            {showRows
              ? slice.map((row) => (
                  <tr key={`${row.source}-${row.id}`}>
                    {visibleColumns.map((col) => (
                      <td key={col.id}>
                        {col.id === "status" ? (
                          <span className={membershipStatusBadgeClass(row.status)}>
                            {row.status || "—"}
                          </span>
                        ) : col.id === "payment_status" ? (
                          <span
                            className={`mm-pay-chip mm-pay-chip--${String(row.payment_status || "none")
                              .toLowerCase()
                              .replace(/\s+/g, "-")}`}
                          >
                            {timelineCellValue(col.id, row)}
                          </span>
                        ) : (
                          timelineCellValue(col.id, row)
                        )}
                      </td>
                    ))}
                    <td className="ub-table__col-actions">
                      <div className="ub-table-actions">
                        <GlobalTableActionButton
                          action="view"
                          type="button"
                          onClick={() => setSelected(row)}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              : null}
          </GlobalDirectoryGridSection>
        }
        pagination={
          !loading && filtered.length > 0 ? (
            <GlobalDirectoryPaginationSection
              page={safePage}
              totalPages={totalPages}
              total={total}
              pageSize={MY_MEMBERSHIPS_PAGE_SIZE}
              onPageChange={setPage}
              label="Membership directory pagination"
            />
          ) : null
        }
      />

      <MyMembershipsDetailModal row={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
